import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Drawer } from '@mui/material';
import { useFreePostInfiniteListQuery } from '../../../queries';
import useInfiniteScroll from '../../../hook/useInfiniteScroll';
import PostCard from './PostCard';
import PostDetail from './PostDetail';

const Sort = {
  Like: 1,
  Latest: 2,
};

const LIMIT = 10;

function FreeBoard() {
  const pushedRef = useRef(false);
  const [contentOpenId, setContentOpenId] = useState(null);
  const [sorting, setSorting] = useState(Sort.Latest);

  // TanStack Query 무한 스크롤 적용
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFreePostInfiniteListQuery({
    limit: LIMIT,
    sortBy: sorting === Sort.Like ? 'likeCount' : 'createdAt',
  });

  const list = useMemo(
    () => data?.pages.flatMap((page: any) => page.posts) || [],
    [data]
  );


  // infinite scroll 연동
  const { lastItemRef } = useInfiniteScroll({
    hasMore: !!hasNextPage,
    isLoading: isFetchingNextPage,
    onLoadMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
  });

  // 뒤로가기에 드로워가 닫히도록 설정
  const openDrawer = useCallback((id) => {
    setContentOpenId(id);
    window.history.pushState({ drawer: true }, '');
    pushedRef.current = true;
  }, []);

  const closeDrawer = useCallback(() => {
    setContentOpenId(null);
    if (pushedRef.current) {
      pushedRef.current = false;
      window.history.back();
    }
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (contentOpenId !== null) {
        setContentOpenId(null);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [contentOpenId]);

  return (
    <div className="relative w-full">
      <div className="w-full flex justify-end items-center my-5 text-[12px] font-[700] gap-2">
        <button
          type="button"
          style={{ color: sorting === Sort.Like ? '#363636' : '#969696' }}
          onClick={() => setSorting(Sort.Like)}
        >
          좋아요순
        </button>
        <button
          type="button"
          style={{ color: sorting === Sort.Latest ? '#363636' : '#969696' }}
          onClick={() => setSorting(Sort.Latest)}
        >
          최신순
        </button>
      </div>
      <div className="w-full">
        {list.length >= 1 &&
          list.map((content: any, idx: number) => {
            return (
              <PostCard
                key={content.id}
                post={content}
                onClick={(id: number) => openDrawer(id)}
                ref={idx === list.length - 1 ? lastItemRef : undefined}
              />
            );
          })}
        {isLoading && (
          <div className="w-full py-5 text-center text-gray-500">
            로딩 중...
          </div>
        )}
      </div>
      <Drawer
        open={contentOpenId !== null}
        onClose={closeDrawer}
        anchor="right"
        sx={{
          '& .MuiDrawer-paper': {
            width: '100vw',
            maxWidth: '100vw',
          },
        }}
      >
        <PostDetail
          drawerOpen={contentOpenId !== null}
          onClickPrev={closeDrawer}
          postId={contentOpenId}
        />
      </Drawer>
    </div>
  );
}

export default FreeBoard;
